// Chat input toolbar component.
// Copied from frontend/src/components/agent/ChatInputToolbar.tsx.
// Generic apart from DisabledReason (the one way a disabled control says why).
// See ../../docs/90-provenance-map.md.

import React, { useCallback, useRef } from 'react'
import { Box, IconButton, Chip, CircularProgress, Collapse, Fade, Tooltip, Typography, alpha } from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import MicIcon from '@mui/icons-material/Mic'
import CloseIcon from '@mui/icons-material/Close'
import type { PendingAttachment } from '../hooks/useFileAttachments.js'
import RecordingOverlay from './RecordingOverlay.js'
import { INNER_RADIUS } from './surface.js'
import DisabledReason from '../../components/system/DisabledReason'

const ACCEPTED_TYPES = '*/*'
/**
 * The recording pill slides open beside the mic and fades in, and leaves the
 * same way (Adam, 14 Sep). The mic stays where it is, disabled while
 * recording, so the row never jumps: the only thing that changes width is the
 * pill, and that width is animated. The microphone picker is not in this row
 * any more; it lives in Session options (useMicrophones).
 */
export const RECORDING_FADE_IN_MS = 220
export const RECORDING_FADE_OUT_MS = 160

interface ChatInputToolbarProps {
  onFilesSelected: (files: File[]) => void
  attachments: PendingAttachment[]
  onRemoveAttachment: (id: string) => void
  isRecording: boolean
  isTranscribing: boolean
  onToggleRecording: () => void
  onStopRecording: () => void
  onCancelRecording: () => void
  stream: MediaStream | null
  error?: string | null
  disabled: boolean
  compact?: boolean
  /**
   * Show the paperclip. Default true. A side pane (SidePaneChat) has nowhere
   * to send a file, so it hides the clip and keeps only the mic.
   */
  showAttach?: boolean
  /**
   * Why the person may not dictate (security audit C23: POST /transcribe
   * needs agent access and team budget). Set, the mic is drawn disabled with
   * the reason, never hidden; the host words it (useDictationAccess).
   */
  micDisabledReason?: string
  /**
   * A short line beside the mic asking the person to say more (guided
   * conversations, after a card pick fills the composer). The host words it
   * and clears it; it hides while recording or transcribing.
   */
  micNudge?: string
}

export default function ChatInputToolbar({
  onFilesSelected,
  attachments,
  onRemoveAttachment,
  isRecording,
  isTranscribing,
  onToggleRecording,
  onStopRecording,
  onCancelRecording,
  stream,
  error,
  disabled,
  compact,
  showAttach = true,
  micDisabledReason,
  micNudge,
}: ChatInputToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      onFilesSelected(Array.from(files))
    }
    e.target.value = ''
  }, [onFilesSelected])

  const iconSize = compact ? 18 : 22
  const btnSize = compact ? 28 : 34

  const micButton = (
    <IconButton
      size="small"
      disabled={disabled || isTranscribing || isRecording}
      onClick={onToggleRecording}
      aria-label={isTranscribing ? 'Transcribing message' : 'Dictate a message'}
      aria-pressed={isRecording}
      sx={{
        width: btnSize,
        height: btnSize,
        color: 'info.main',
        transition: 'background-color 160ms ease-out',
        // Recording: the mic reads as "on", not "unavailable" — the
        // icon keeps its colour on a tinted disc.
        ...(isRecording ? {
          '&&.Mui-disabled': { color: 'info.main', bgcolor: theme => alpha(theme.palette.info.main, 0.12) },
        } : {}),
      }}
    >
      {isTranscribing ? (
        <CircularProgress size={iconSize - 4} />
      ) : (
        <MicIcon sx={{ fontSize: iconSize }} />
      )}
    </IconButton>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {/* Buttons row + inline attachment previews. While recording, the
          pill slides open beside the mic (Adam, 14 Sep); nothing else in
          the row moves or disappears. */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        {showAttach && (
          <>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_TYPES}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            {/* Paperclip button */}
            <Tooltip title="Attach files">
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Attach a file"
                  sx={{ width: btnSize, height: btnSize, color: 'info.main' }}
                >
                  <AttachFileIcon sx={{ fontSize: iconSize }} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}

        {/* Mic button: stays put while recording, pressed and disabled. A
            person who may not dictate sees it disabled with the reason. */}
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {micDisabledReason ? (
            <DisabledReason reason={micDisabledReason}>
              {micButton}
            </DisabledReason>
          ) : (
            <Tooltip title={isTranscribing ? 'Transcribing...' : isRecording ? 'Recording' : 'Voice input'}>
              <span>{micButton}</span>
            </Tooltip>
          )}
        </Box>

        {micNudge && !isRecording && !isTranscribing && (
          <Typography component="span" role="status" sx={{ fontSize: 12, color: 'info.main', px: 0.5 }}>
            {micNudge}
          </Typography>
        )}

        {/* The recording pill, beside the mic. Collapse animates its width
            from nothing to its own size and back, so the row grows and
            shrinks under it; Fade carries the opacity. Both share one clock
            so the pill is gone the moment the row has closed. */}
        <Collapse
          in={isRecording}
          orientation="horizontal"
          timeout={{ enter: RECORDING_FADE_IN_MS, exit: RECORDING_FADE_OUT_MS }}
          mountOnEnter
          unmountOnExit
          sx={{ flexShrink: 0 }}
        >
          <Fade in={isRecording} appear timeout={{ enter: RECORDING_FADE_IN_MS, exit: RECORDING_FADE_OUT_MS }}>
            <span style={{ display: 'inline-flex' }}>
              <RecordingOverlay
                stream={stream}
                isRecording={isRecording}
                onStop={onStopRecording}
                onCancel={onCancelRecording}
              />
            </span>
          </Fade>
        </Collapse>

        {/* Inline attachment previews */}
        {attachments.map(a => (
          <Box key={a.id} sx={{ position: 'relative', display: 'inline-flex' }}>
            {a.previewUrl ? (
              <Box sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={a.previewUrl}
                  alt={a.file.name}
                  sx={{
                    width: compact ? 28 : 34,
                    height: compact ? 28 : 34,
                    borderRadius: INNER_RADIUS,
                    objectFit: 'cover',
                    border: '1px solid #e5e7eb',
                    opacity: a.uploadState === 'uploading' ? 0.5 : 1,
                  }}
                />
                {a.uploadState === 'uploading' && (
                  <CircularProgress
                    size={14}
                    sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-7px', ml: '-7px' }}
                  />
                )}
                <IconButton
                  size="small"
                  onClick={() => onRemoveAttachment(a.id)}
                  aria-label={`Remove ${a.file.name}`}
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    width: 16,
                    height: 16,
                    backgroundColor: '#fff',
                    border: '1px solid #d1d5db',
                    '&:hover': { backgroundColor: '#f3f4f6' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 10 }} />
                </IconButton>
              </Box>
            ) : (
              <Chip
                label={a.file.name}
                size="small"
                onDelete={() => onRemoveAttachment(a.id)}
                color={a.uploadState === 'error' ? 'error' : 'default'}
                sx={{ fontSize: compact ? 11 : 12, maxWidth: 160 }}
              />
            )}
          </Box>
        ))}
      </Box>

      {/* Error display */}
      {error && (
        <Typography sx={{ fontSize: compact ? 11 : 12, color: '#ef4444', px: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  )
}
